import {
  loadFixture, paginate, findById, findInTree, removeFromTree, nextId, parseBody, parseQuery,
  mergeEntity, urlParam, CURRENT_USER_PROFILE_REF, groupIdentifierFromUrl, groupFixtureName,
} from '../mockDataStore';

const EVENT_LIST = /^\/?api\/v1\/groups\/[\w-]+\/group-events\/?(\?.*)?$/;
const EVENT_DETAIL = /^\/?api\/v1\/groups\/[\w-]+\/group-events\/(\d+)\/?$/;
const ATTENDEES_LIST = /^\/?api\/v1\/groups\/[\w-]+\/group-events\/\d+\/event-attendees\/?(\?.*)?$/;
const ATTENDEES_EXPORT = /^\/?api\/v1\/groups\/[\w-]+\/group-events\/\d+\/attendees-export\/?(\?.*)?$/;
const EXPORT_STATUSES = { attending: 'Attending', not_attending: 'Unable to Attend', no_response: null };
const DISCUSSIONS_LIST = /^\/?api\/v1\/groups\/[\w-]+\/group-events\/\d+\/event-discussions\/?(\?.*)?$/;
const DISCUSSION_DETAIL = /^\/?api\/v1\/groups\/[\w-]+\/group-events\/\d+\/event-discussions\/(\d+)\/?$/;
const DISCUSSION_LIKE = /^\/?api\/v1\/groups\/[\w-]+\/group-events\/\d+\/event-discussions\/(\d+)\/like\/?$/;

export default function registerEventMocks(mock) {
  mock.onGet(EVENT_LIST).reply(async (config) => {
    const events = await loadFixture(groupFixtureName('events/events', groupIdentifierFromUrl(config.url)));
    return [200, paginate(events.results, parseQuery(config.url))];
  });

  mock.onPost(EVENT_LIST).reply(async (config) => {
    const events = await loadFixture(groupFixtureName('events/events', groupIdentifierFromUrl(config.url)));
    const created = { id: nextId(events.results), ...parseBody(config) };
    events.results.push(created);
    events.count += 1;
    return [201, created];
  });

  mock.onGet(EVENT_DETAIL).reply(async (config) => {
    const events = await loadFixture(groupFixtureName('events/events', groupIdentifierFromUrl(config.url)));
    const event = findById(events.results, Number(urlParam(config, EVENT_DETAIL)));
    return event ? [200, event] : [404, { message: 'Not found.' }];
  });

  mock.onPut(EVENT_DETAIL).reply(async (config) => {
    const events = await loadFixture(groupFixtureName('events/events', groupIdentifierFromUrl(config.url)));
    const event = findById(events.results, Number(urlParam(config, EVENT_DETAIL)));
    if (!event) return [404, { message: 'Not found.' }];
    mergeEntity(event, parseBody(config), ['photo']);
    return [200, event];
  });

  mock.onDelete(EVENT_DETAIL).reply(async (config) => {
    const events = await loadFixture(groupFixtureName('events/events', groupIdentifierFromUrl(config.url)));
    const id = Number(urlParam(config, EVENT_DETAIL));
    events.results = events.results.filter((event) => event.id !== id);
    events.count = events.results.length;
    return [200, {}];
  });

  mock.onGet(ATTENDEES_LIST).reply(async (config) => {
    const attendees = await loadFixture(
      groupFixtureName('events/event-attendees', groupIdentifierFromUrl(config.url)),
    );
    return [200, paginate(attendees.results, parseQuery(config.url))];
  });

  mock.onPost(ATTENDEES_LIST).reply(async (config) => {
    const groupId = groupIdentifierFromUrl(config.url);
    const attendees = await loadFixture(groupFixtureName('events/event-attendees', groupId));
    const body = parseBody(config);
    // The attendee record needs the profile's name/picture flattened onto it directly (see
    // schema doc) — look the profile up rather than trusting the demo persona by default,
    // since group admins can RSVP on behalf of another member.
    const profiles = await loadFixture(groupFixtureName('groups/profiles', groupId));
    const profile = body.profile
      ? profiles.results.find((item) => item.id === Number(body.profile))
      : null;
    const created = {
      is_on_my_list: false,
      first_name: profile?.first_name,
      last_name: profile?.last_name,
      profile_picture: profile?.profile_picture,
      ...body,
      id: nextId(attendees.results),
    };
    attendees.results.push(created);
    attendees.count += 1;
    return [201, created];
  });

  // A simplified version of the API's RSVP export: same columns, built from the fixture.
  mock.onGet(ATTENDEES_EXPORT).reply(async (config) => {
    const attendees = await loadFixture(
      groupFixtureName('events/event-attendees', groupIdentifierFromUrl(config.url)),
    );
    const status = (config.params && config.params.status) || 'all';
    const rows = attendees.results.filter(
      (person) => status === 'all' || (person.rsvp || null) === EXPORT_STATUSES[status],
    );
    const going = (person) => person.rsvp === 'Attending';
    const lines = [
      'Name,Email,Phone,City,State,Member or public guest,RSVP,Guests,Total people,RSVP date',
      ...rows.map((person) => [
        `${person.first_name} ${person.last_name}`, '', '', '', '', 'Member',
        person.rsvp || 'No response',
        going(person) ? person.guests_amount || 0 : 0,
        going(person) ? (person.guests_amount || 0) + 1 : 0,
        '',
      ].join(',')),
      '',
      `Total attending (including guests),${attendees.results.filter(going)
        .reduce((total, person) => total + (person.guests_amount || 0) + 1, 0)}`,
      'Capacity,No limit',
    ];
    return [200, `${lines.join('\r\n')}\r\n`, { 'content-disposition': 'attachment; filename="Event RSVPs.csv"' }];
  });

  mock.onGet(DISCUSSIONS_LIST).reply(async (config) => {
    const discussions = await loadFixture(
      groupFixtureName('events/event-discussions', groupIdentifierFromUrl(config.url)),
    );
    return [200, paginate(discussions.results, parseQuery(config.url))];
  });

  mock.onPost(DISCUSSIONS_LIST).reply(async (config) => {
    const discussions = await loadFixture(
      groupFixtureName('events/event-discussions', groupIdentifierFromUrl(config.url)),
    );
    const body = parseBody(config);
    const created = {
      id: nextId(discussions.results.flatMap((post) => [post, ...post.sub_discussions])),
      content: body.content || '',
      photo: body.photo instanceof Blob ? URL.createObjectURL(body.photo) : null,
      posted_datetime: new Date().toISOString(),
      profile: CURRENT_USER_PROFILE_REF,
      likes: [],
      sub_discussions: [],
    };

    // A `parent_post` means this is a reply — nest it under that post's sub_discussions
    // (matching the tree shape GET returns) instead of appending a new top-level post.
    const parentId = body.parent_post ? Number(body.parent_post) : null;
    if (parentId) {
      const parent = findInTree(discussions.results, parentId);
      if (!parent) return [404, { message: 'Not found.' }];
      parent.sub_discussions.push(created);
    } else {
      discussions.results.push(created);
      discussions.count += 1;
    }

    return [201, created];
  });

  mock.onDelete(DISCUSSION_DETAIL).reply(async (config) => {
    const discussions = await loadFixture(
      groupFixtureName('events/event-discussions', groupIdentifierFromUrl(config.url)),
    );
    removeFromTree(discussions.results, Number(urlParam(config, DISCUSSION_DETAIL)));
    discussions.count = discussions.results.length;
    return [200, {}];
  });

  mock.onPost(DISCUSSION_LIKE).reply(async (config) => {
    const discussions = await loadFixture(
      groupFixtureName('events/event-discussions', groupIdentifierFromUrl(config.url)),
    );
    const post = findInTree(discussions.results, Number(urlParam(config, DISCUSSION_LIKE)));
    if (!post) return [404, { message: 'Not found.' }];
    if (!post.likes.some((like) => like.profile.id === CURRENT_USER_PROFILE_REF.id)) {
      post.likes.push({ profile: CURRENT_USER_PROFILE_REF });
    }
    return [200, post];
  });

  mock.onDelete(DISCUSSION_LIKE).reply(async (config) => {
    const discussions = await loadFixture(
      groupFixtureName('events/event-discussions', groupIdentifierFromUrl(config.url)),
    );
    const post = findInTree(discussions.results, Number(urlParam(config, DISCUSSION_LIKE)));
    if (!post) return [404, { message: 'Not found.' }];
    post.likes = post.likes.filter((like) => like.profile.id !== CURRENT_USER_PROFILE_REF.id);
    return [200, post];
  });
}
